/**
 * auth-context — the single, stable container for request-time auth state.
 * (WarpDrive migration ADR-001; see docs/warp-drive/ADR-001-request-layer-state.md.)
 *
 * THE RULE: nothing in the request layer may capture auth state (or functions
 * that read it) at construction time. Handlers read `authContext.*()` at
 * REQUEST time; every read dereferences the live providers through this
 * container, so installing (or re-installing) providers propagates to every
 * reader instantly. That makes the spike's dead-401 class — a config object
 * capturing `let` bindings by value before a setter rebinds them —
 * impossible by construction: there is no value capture anywhere.
 *
 * ONE WRITER: the app shell (each app's store service today; the session
 * layer after Phase 2/3) installs providers exactly once at boot via
 * `installAuthContextProviders`. tio-data never writes it.
 *
 * Framework-free on purpose: no Ember imports, no DI, no owner. The
 * providers are plain functions the app creates over its own services.
 */

export interface AuthContextProviders {
  /** Current access token, or undefined when logged out. */
  token: () => string | undefined;
  isAuthenticated: () => boolean;
  /** AuthKey#public_key sent as `x-auth-key`, when known. */
  publicKey?: () => string | undefined;
  /** Epoch ms the session expires at — drives proactive renewal. */
  sessionExpiresAt?: () => number | undefined;
  /**
   * Renewal port: the ACTION lives with the session owner; the request
   * layer only decides WHEN (threshold/cooldown bookkeeping stays in the
   * auth handler).
   */
  renew?: (token?: string) => Promise<unknown> | unknown;
  /** Observability context for error reporting (never sent on requests). */
  userId?: () => string | undefined;
}

const NO_PROVIDERS: Required<Pick<AuthContextProviders, 'token' | 'isAuthenticated'>> &
  AuthContextProviders = {
  token: () => undefined,
  isAuthenticated: () => false,
};

const current: { providers: AuthContextProviders } = { providers: NO_PROVIDERS };

export function installAuthContextProviders(providers: AuthContextProviders): void {
  current.providers = providers;
}

/** Test helper — restores the logged-out defaults. */
export function resetAuthContext(): void {
  current.providers = NO_PROVIDERS;
}

export const authContext = {
  token: (): string | undefined => current.providers.token(),
  isAuthenticated: (): boolean => current.providers.isAuthenticated(),
  publicKey: (): string | undefined => current.providers.publicKey?.(),
  sessionExpiresAt: (): number | undefined => current.providers.sessionExpiresAt?.(),
  hasRenew: (): boolean => typeof current.providers.renew === 'function',
  renew: (token?: string): Promise<unknown> | unknown => {
    const renew = current.providers.renew;
    if (!renew) {
      throw new Error('Session renewal requested but no renew provider is installed');
    }
    return renew(token);
  },
  userId: (): string | undefined => current.providers.userId?.(),
} as const;
